import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';

import { NedarimMosadCredentialDtoIn } from '@/models/in/NedarimMosadCredentialDtoIn';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col, Cell } from '@/components/ui/DataTable/ColumnsHelpers';
import cellType from '@/components/ui/DataTable/CellTypes';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import useTableDensity from '@/hooks/useTableDensity';
import SecretDialog from './SecretDialog';

const QUERY_URL = 'management/nedarim-credentials';
const DENSITY_STORAGE_KEY = 'table.density.nedarimCredentials';

const NedarimCredentialsManagementPage = () => {
  const { t } = useTranslation();

  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');

  const { data: rows, isLoading, refetch } = useGetAuthApi<NedarimMosadCredentialDtoIn[]>(QUERY_URL);

  /**
   * The key's health. A mosad still reading its password from configuration is not "missing
   * a key" - it has one, just not one anybody can change from here, which is what the
   * source column says instead.
   */
  const renderKeyStatus = (row: NedarimMosadCredentialDtoIn) => {
    const status = !row.hasApiPassword ? (row.passwordSource === 'configuration' ? 'ok' : 'missing') : row.isValid ? 'ok' : 'invalid';
    const color = status === 'ok' ? 'bg-green-500' : status === 'invalid' ? 'bg-orange-400' : 'bg-gray-400';
    return (
      <div className="flex items-center gap-2">
        <div className={`w-[10px] h-[10px] rounded-full ${color}`} />
        <span>{t(`nedarimCredentials.keyStatus.${status}`)}</span>
      </div>
    );
  };

  /**
   * Where the password is read from. `configuration` is the one worth spotting: that secret
   * still lives in an App Service environment variable, so only a deploy can change it.
   */
  const renderPasswordSource = (row: NedarimMosadCredentialDtoIn) => (
    <span className={row.passwordSource === 'configuration' ? 'text-orange-500' : ''} title={t(`nedarimCredentials.source.${row.passwordSource}Hint`)}>
      {t(`nedarimCredentials.source.${row.passwordSource}`)}
    </span>
  );

  /**
   * Whether the system may replace this key by itself. It is the difference between a
   * rejection that heals on the next run and one that waits for someone to paste a new key
   * in - which is exactly what nobody could see while the system answered every rejection
   * by minting another key.
   */
  const renderOwnership = (row: NedarimMosadCredentialDtoIn) => {
    const kind = row.isSelfManaged ? 'auto' : 'manual';
    return (
      <span className={row.isSelfManaged ? '' : 'text-(--font-body-1) font-semibold'} title={t(`nedarimCredentials.ownership.${kind}Hint`)}>
        {t(`nedarimCredentials.ownership.${kind}`)}
      </span>
    );
  };

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(
      (r) => String(r.mosadId).includes(query) || (r.fundId != null && String(r.fundId).includes(query)) || (r.mosadName ?? '').toLowerCase().includes(query)
    );
  }, [rows, searchQuery]);

  const columns: ColumnDef<NedarimMosadCredentialDtoIn>[] = [
    {
      id: 'mosadId',
      header: Col(t('nedarimCredentials.columns.mosadId')),
      accessorKey: 'mosadId',
      enableSorting: true,
      meta: { style: { width: '90px', maxWidth: '90px' } },
    },
    {
      id: 'fundId',
      header: Col(t('nedarimCredentials.columns.fundId')),
      accessorKey: 'fundId',
      enableSorting: true,
      meta: { style: { width: '70px', maxWidth: '70px' } },
    },
    {
      id: 'mosadName',
      header: Col(t('nedarimCredentials.columns.mosadName')),
      accessorKey: 'mosadName',
      enableSorting: true,
      cell: ({ row }) => <span title={row.original.mosadName ?? ''}>{row.original.mosadName}</span>,
      meta: { style: { width: '320px', maxWidth: '320px' } },
    },
    {
      id: 'apiPassword',
      header: Col(t('nedarimCredentials.columns.apiPassword')),
      enableSorting: false,
      cell: ({ row }) => (
        <SecretDialog
          mosadId={row.original.mosadId}
          mosadName={row.original.mosadName}
          field="apiPassword"
          has={row.original.hasApiPassword}
          label={t('nedarimCredentials.columns.apiPassword')}
          onSaved={refetch}
        />
      ),
      meta: { style: { width: '80px', maxWidth: '80px' } },
    },
    {
      id: 'apiValid',
      header: Col(t('nedarimCredentials.columns.apiValid')),
      enableSorting: false,
      cell: ({ row }) => (
        <SecretDialog
          mosadId={row.original.mosadId}
          mosadName={row.original.mosadName}
          field="apiValid"
          has={row.original.hasApiValid}
          label={t('nedarimCredentials.columns.apiValid')}
          onSaved={refetch}
        />
      ),
      meta: { style: { width: '80px', maxWidth: '80px' } },
    },
    {
      id: 'keyStatus',
      header: Col(t('nedarimCredentials.columns.keyStatus')),
      enableSorting: false,
      cell: ({ row }) => renderKeyStatus(row.original),
      meta: { style: { width: '130px', maxWidth: '130px' } },
    },
    {
      id: 'passwordSource',
      header: Col(t('nedarimCredentials.columns.passwordSource')),
      accessorKey: 'passwordSource',
      enableSorting: true,
      cell: ({ row }) => renderPasswordSource(row.original),
      meta: { style: { width: '110px', maxWidth: '110px' } },
    },
    {
      id: 'ownership',
      header: Col(t('nedarimCredentials.columns.ownership')),
      accessorKey: 'isSelfManaged',
      enableSorting: true,
      cell: ({ row }) => renderOwnership(row.original),
      meta: { style: { width: '110px', maxWidth: '110px' } },
    },
    {
      id: 'isActive',
      header: Col(t('nedarimCredentials.columns.isActive')),
      accessorKey: 'isActive',
      enableSorting: true,
      cell: ({ row }) => <span>{row.original.isActive ? t('nedarimCredentials.active') : t('nedarimCredentials.inactive')}</span>,
      meta: { style: { width: '80px', maxWidth: '80px' } },
    },
    {
      id: 'failures',
      header: Col(t('nedarimCredentials.columns.failures')),
      accessorKey: 'provisionFailureCount',
      enableSorting: true,
      cell: ({ row }) => (row.original.provisionFailureCount > 0 ? <span className="text-red-500">{row.original.provisionFailureCount}</span> : <span>—</span>),
      meta: { style: { width: '90px', maxWidth: '90px' } },
    },
    {
      // A mosad Nedarim keep refusing. Non-zero used to be invisible - the sync minted a
      // replacement, the retry succeeded, the run went green, and the count of permanent
      // keys in the portal grew by one every run.
      id: 'rejections',
      header: Col(t('nedarimCredentials.columns.rejections')),
      accessorKey: 'keyRejectionCount',
      enableSorting: true,
      cell: ({ row }) => (row.original.keyRejectionCount > 0 ? <span className="text-red-500">{row.original.keyRejectionCount}</span> : <span>—</span>),
      meta: { style: { width: '80px', maxWidth: '80px' } },
    },
    {
      id: 'updatedAt',
      header: Col(t('nedarimCredentials.columns.updatedAt')),
      accessorKey: 'updatedAt',
      enableSorting: true,
      cell: Cell(cellType.Date),
      meta: { style: { width: '110px', maxWidth: '110px' } },
    },
  ];

  return (
    <div className="w-full">
      <DataTable
        title={t('nedarimCredentials.title')}
        data={filteredRows}
        isLoading={isLoading}
        columns={columns}
        sortBy={{ id: 'mosadId', desc: false }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        density={density}
        onDensityChange={setDensity}
        columnLayoutKey="nedarimCredentials"
        searchPlaceholder="nedarimCredentials.search"
        customGlobalSearch={() => true}
        emptyStateContent={{
          title: t('nedarimCredentials.emptyTitle'),
          description: t('nedarimCredentials.emptyDescription'),
        }}
      />
    </div>
  );
};

export default NedarimCredentialsManagementPage;
